import * as React from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  removeAllBusinessReportOutcomeResults,
  selectCompareResults,
  addBusinessReportOutcomeResult,
} from "@/store/slices/snapshotCompareSlice";
import { GridSidebarComparisonListRow } from "@/modules/snapshots/grid/sidebar/comparison/list-row";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ulid } from "ulid";
import { useForm } from "react-hook-form";

interface CreateRowFormData {
  content: string;
}

export const GridSidebarComparisonList: React.FC = () => {
  const results = useAppSelector(selectCompareResults);
  const dispatch = useAppDispatch();
  const [isCreatingRow, setIsCreatingRow] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateRowFormData>({
    mode: "onChange",
    defaultValues: {
      content: "",
    },
  });

  const handleRemoveAll = () => {
    dispatch(removeAllBusinessReportOutcomeResults());
  };

  const onSubmit = (data: CreateRowFormData) => {
    dispatch(
      addBusinessReportOutcomeResult({
        id: ulid(),
        name: "Custom Row",
        result: data.content.trim(),
        documentName: "Custom",
        business_report_prompt_id: "",
        prompt: "",
        order: results.length,
        date: null,
        is_date: false,
        data_type: "categorical",
        edited_by_id: null,
        edited_at: null,
        is_note: false,
        error_message: null,
        traffic_light: false,
        rating: null,
      }),
    );
    reset();
    setIsCreatingRow(false);
  };

  const handleCancelCreate = () => {
    reset();
    setIsCreatingRow(false);
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between gap-2">
        <Button onClick={() => setIsCreatingRow(true)} size="sm" variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Add Row
        </Button>
        {results.length > 0 && (
          <Button onClick={handleRemoveAll} size="xs" variant="destructive">
            Remove all
          </Button>
        )}
      </div>

      {isCreatingRow && (
        <form onSubmit={handleSubmit(onSubmit)} className="mb-4 rounded-lg border bg-white p-4">
          <h4 className="mb-3 text-sm font-semibold">Create New Row</h4>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Content</label>
              <textarea
                {...register("content", {
                  required: "Content is required",
                  validate: value => value.trim().length > 0 || "Content cannot be empty",
                })}
                placeholder="Enter content (supports Markdown)..."
                rows={6}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.content && <p className="mt-1 text-xs text-red-600">{errors.content.message}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={handleCancelCreate} size="sm" variant="outline">
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!isValid}>
                Create
              </Button>
            </div>
          </div>
        </form>
      )}

      {results.length === 0 && !isCreatingRow ? (
        <div className="p-4 text-center text-gray-500">No items in comparison list</div>
      ) : (
        <div className="space-y-3">
          {results.map(result => (
            <GridSidebarComparisonListRow result={result} key={result.id} />
          ))}
        </div>
      )}
    </div>
  );
};
